import { Api, SignalCategory } from '@firmable/contracts';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@web/constants/query';
import * as Signals from '@web/contracts/Signals';
import { Signal } from '@web/store/Signal';
import axios from 'axios';
import { Failure } from './useDownloadHistory';

export interface QueryParams {
  activePage: number;
  filter: Signals.Records.Filters | null;
  enabled: boolean;
  category: SignalCategory;
  agentId?: number | 'default' | 'default-intent';
  agentRunId?: string;
}

export interface ApiResponse {
  success: boolean;
  total: number;
  records: Api.Signals.SignalItem[];
}

interface SignalsRequestPayload {
  agentId?: number | 'default' | 'default-intent';
  category: SignalCategory;
  from: number;
  size: number;
  filters: Record<string, string | string[]>;
  sort?: Array<{ identifier: string; order: 'asc' | 'desc' }>;
  agentRunId?: string;
}

export const useSignals = ({
  activePage: nextPage,
  filter,
  enabled = true,
  category = 'company',
  agentId,
  agentRunId,
}: Partial<QueryParams>) => {
  const { pageIndex, pageSize, sorting, search } = Signal.List.use.getState();

  const _category = category ?? 'company';
  const activePage = nextPage ?? pageIndex[_category];
  const size = pageSize[_category];
  const from = size * activePage;

  const sortEntries = Object.entries(sorting?.[_category] ?? {}).map(([identifier, order]) => ({
    identifier,
    order,
  }));

  const signalTypeKey =
    _category === 'company' ? 'company_signal_type' : _category === 'people' ? 'people_signal_type' : 'topic_name';
  const signalsMap =
    _category === 'company'
      ? filter?.companySignals
      : _category === 'people'
        ? filter?.peopleSignals
        : filter?.intentTopics;
  const [signalType] = Object.keys(signalsMap ?? {});

  const searchForCategory = (search?.[_category] ?? {}) as Record<string, string>;

  const filters: Record<string, string | string[]> = { ...searchForCategory };
  if (signalType) {
    filters[signalTypeKey] = signalType;
  }

  const ACTION_PERFORM_VALUES: Api.Signals.ActionPerform[] = [
    'task_created',
    'record_created',
    'note_created',
    'webhook_pushed',
    'no_action_performed',
  ];
  const agentActionsFilter = searchForCategory['action_perform'];
  if (ACTION_PERFORM_VALUES.includes(agentActionsFilter as Api.Signals.ActionPerform)) {
    filters['action_perform'] = [agentActionsFilter];
  }

  const [incrementalRunId] = filter?.incrementalRunIds ?? [];
  if (incrementalRunId) {
    filters['incremental_run_id'] = incrementalRunId;
  }

  const payload: SignalsRequestPayload = {
    agentId: agentId,
    category: _category,
    from,
    size,
    filters,
    sort: sortEntries,
  };

  const resolvedAgentRunId = agentRunId ?? filter?.agentRunId;
  if (resolvedAgentRunId !== undefined) {
    payload.agentRunId = resolvedAgentRunId;
  }

  return useQuery<ApiResponse, Failure>({
    queryKey: [Query.SupabaseKeys['signals'], { payload }],
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    enabled: enabled,
    queryFn: async () => {
      const { data } = await axios.post<ApiResponse>(
        `${process.env.NEXT_PUBLIC_API_APP_ENDPOINT}/signals/records`,
        payload,
      );

      return data;
    },
  });
};
